import API from 'lib/tessitura/API';
import { ERROR_CONSTANTS } from 'constants';
import { partition, uniq, isObject } from 'lodash';
import combineSubLineItems from 'lib/cart/combineSubLineItems';

function normalizeErrorCode(errorMessage, fallbackMessage) {
  if (
    errorMessage.indexOf(
      'One or more of the selected seats is not available'
    ) !== -1
  ) {
    return ERROR_CONSTANTS.SEATS_UNAVAILABLE;
  }

  if (
    errorMessage.indexOf('This request will exceed the ticket limit') !== -1
  ) {
    return 'You have exceeded the ticket limit, please reduce your order to proceed.';
  }

  if (
    errorMessage.indexOf('This seat combination would leave single seats') !==
    -1
  ) {
    return (
      '<h3>Oops!</h3>One seat has been stranded at the end of a row or stuck between selected seats.' +
      ' Please leave at least two empty seats together.'
    );
  }

  return fallbackMessage;
}

export function getCartLineItems(cart) {
  let lineSeqNumbers = cart.map(x => x.li_seq_no);
  return uniq(lineSeqNumbers);
}

export function filterRemainingItems(cart, newSeats) {
  let filteredByNewSeats = cart.filter(item => {
    let newSeatIds = newSeats.map(x => x.seatData.seat_no);
    return newSeatIds.indexOf(parseInt(item.seat_no, 10)) !== -1;
  });

  return uniq(filteredByNewSeats).map(item => item.li_seq_no);
}

export function partitionExistingItems(lineItems, cart) {
  return partition(lineItems, item => {
    let cartIds = cart.map(x => parseInt(x.seat_no, 10));
    return cartIds.indexOf(item.seatData.seat_no) === -1;
  });
}

function releaseAll(perfNo, items, currentFixedPackage) {
  return Promise.all(
    items.map(function(item) {
      return API.ReleaseTickets(perfNo, item, currentFixedPackage);
    })
  );
}

function processNewFixedPackage(packageNo, lineItems) {
  let messages = {
    error: 'There was a problem adding your package.'
  };

  return new Promise((resolve, reject) => {
    API.AddPackageItemSeated(
      {
        pkg_no: packageNo,
        zone_no: 0
      },
      lineItems
    )
      .then(response => {
        let result;
        if (isObject(response.result)) {
          result = parseInt(response.result[0], 10);
        } else {
          result = parseInt(response.result, 10);
        }

        if (result === lineItems.length) {
          resolve(lineItems);
        } else {
          reject(new Error(messages.error));
        }
      })
      .catch(() => {
        reject(new Error(messages.error));
      });
  });
}

/**
 * Fixed package
 */
function processExistingFixedPackage(
  currentFixedPackage,
  lineItems,
  existingSeats
) {
  let messages = {
    partialError: 'There was a problem amending your seats.',
    removalError: 'There was an error, please try again.'
  };

  return new Promise((resolve, reject) => {
    API.RemovePackageItem(
      currentFixedPackage.pkg_no,
      currentFixedPackage.li_seq_no
    )
      .then(() => {
        API.AddPackageItemSeated(currentFixedPackage, lineItems)
          .then(response => {
            let result;
            if (isObject(response.result)) {
              result = parseInt(response.result[0], 10);
            } else {
              result = parseInt(response.result, 10);
            }

            /**
             * Failed to reserve all of your seats
             * but not a tessitura error.
             */
            if (result === 0) {
              API.AddPackageItemSeated(currentFixedPackage, existingSeats).then(
                resolve
              );
            }

            /**
             * Added all seats
             * or, added some but not others.
             */
            if (result === lineItems.length) {
              resolve(lineItems);
            } else {
              reject(new Error(messages.partialError));
            }
          })
          .catch(() => {
            reject(new Error(messages.partialError));
          });
      })
      .catch(() => {
        reject(new Error(messages.removalError));
      });
  });
}

/**
 * Single Seats
 */
function processAmendSingleSeats(
  perfNo,
  lineItems,
  cartItems,
  currentFixedPackage
) {
  let [newSeats, existingSeats] = partitionExistingItems(lineItems, cartItems);
  let currentLineItems = getCartLineItems(cartItems);

  let messages = {
    reserveError:
      'Oops! \n One seat has been stranded at the end of a row or stuck between selected seats.' +
      ' Please leave at least two empty seats together.'
  };

  if (newSeats.length) {
    return new Promise(function(resolve, reject) {
      /**
       * Release old line items
       */
      releaseAll(perfNo, currentLineItems, currentFixedPackage)
        .then(() => {
          /**
           * Reserve new seats
           */
          API.ReserveTicketsSpecifiedSeats(
            perfNo,
            newSeats,
            currentFixedPackage
          ).then(
            response => {
              let result;
              if (isObject(response.result)) {
                result = parseInt(response.result[0], 10);
              } else {
                result = parseInt(response.result, 10);
              }

              if (result === 0) {
                let newErr = new Error('Seats not added');
                newErr.response = response.error;
                reject(newErr);
              }

              if (result === newSeats.length) {
                /**
                 * If we have existing seats from our original line item re-add them
                 */
                if (existingSeats.length) {
                  API.ReserveTicketsSpecifiedSeats(perfNo, existingSeats)
                    .then(resolve)
                    .catch(() => {
                      reject(new Error(messages.reserveError));
                    });
                } else {
                  resolve(response);
                }
              } else {
                API.getCart()
                  .then(newCart => {
                    let newItems = combineSubLineItems(newCart, perfNo);
                    let remainingLineItems = filterRemainingItems(
                      newItems,
                      newSeats
                    );
                    releaseAll(perfNo, remainingLineItems, currentFixedPackage)
                      .then(resolve)
                      .catch(() => {
                        reject(new Error(messages.reserveError));
                      });
                  })
                  .catch(() => {
                    reject(new Error(messages.reserveError));
                  });
              }
            },
            errResponse => {
              reject(
                new Error(
                  normalizeErrorCode(errResponse.message, messages.reserveError)
                )
              );
            }
          );
        })
        .catch(() => {
          reject(new Error(messages.reserveError));
        });
    });
  } else {
    return new Promise(function(resolve, reject) {
      releaseAll(perfNo, currentLineItems, currentFixedPackage).then(
        () => {
          API.ReserveTicketsSpecifiedSeats(perfNo, existingSeats).then(
            resolve,
            () => {
              reject(new Error(messages.reserveError));
            }
          );
        },
        () => {
          reject(new Error(messages.reserveError));
        }
      );
    });
  }
}

function processNewSeats(perfNo, selections) {
  return new Promise((resolve, reject) => {
    API.ReserveTicketsSpecifiedSeats(perfNo, selections).then(
      successResponse => resolve(successResponse),
      errResponse => {
        reject(
          new Error(
            normalizeErrorCode(
              errResponse.message,
              'There was a problem reserving your seats.'
            )
          )
        );
      }
    );
  });
}

/**
 * Process Mini Cart
 */
export default function processMiniCart(
  cart,
  cartItems,
  lineItems,
  performanceNo,
  packageNo,
  currentFixedPackage
) {
  /**
   * If we don't have an existing cart then this is a
   * new session and we can just reserve tickets.
   */
  if (!cartItems.length) {
    if (packageNo) {
      /**
       * If we are choosing seats for a new package
       */
      return processNewFixedPackage(packageNo, lineItems);
    } else {
      /**
       * If we are choosing seats for a new performance
       */
      return processNewSeats(performanceNo, lineItems);
    }
  } else if (currentFixedPackage) {
    /**
     * If we are editing a fixed package performance
     */
    return processExistingFixedPackage(currentFixedPackage, lineItems);
  } else {
    /**
     * If we are editing single seats
     *
     * Note: Editing fixed package seats at the performance level
     *       is included here as package seats are locked and
     *       excluded from processing which lets us treat add on
     *       seats simply like single seats.
     *
     */
    return processAmendSingleSeats(
      performanceNo,
      lineItems,
      cartItems,
      packageNo
    );
  }
}



// WEBPACK FOOTER //
// ./src/javascripts/lib/cart/processMiniCart.js